import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Upload, Download, FileSpreadsheet, AlertTriangle, CheckCircle2, XCircle, Lightbulb, Sparkles, Wallet, Wand2, X, Droplet, Info, BookOpen } from 'lucide-react';
import CsvColumnMapper from '@/components/CsvColumnMapper';
import { ACCOUNT_TYPES, DEFAULT_ACCOUNT_TYPE } from '@/lib/accountTypes';
import { BROKERS } from '@/lib/brokers';
import { showUndoToast } from '@/lib/undoToast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * CsvImportModal
 * --------------
 * 3-step CSV import flow:
 *   1. Pick file + choose mode (Overwrite vs Append, mutually exclusive,
 *      one MUST be selected to proceed).
 *   2. Preview parsed rows with valid / suggestion / invalid markers.
 *   3. Confirm → POST /portfolio/csv-import → success modal.
 *
 * Triggers `portfolio-changed` after import so PortfolioView /
 * PortfolioSummary refresh, and scrolls the page to the top so the
 * user lands directly on their freshly-imported portfolio.
 */
export default function CsvImportModal({ open, onOpenChange, portfolioId = null, portfolioName = '', onClosedWithoutImport = null }) {
  const [step, setStep] = useState('pick');           // pick → preview → done
  const [mode, setMode] = useState(null);             // 'overwrite' | 'append'
  const [file, setFile] = useState(null);
  const [parseLoading, setParseLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [acceptedSuggestions, setAcceptedSuggestions] = useState({});  // { row_num: true }
  // Round 70 (Feb 2026): Robinhood imports surface cash transactions
  // (IRA contributions, IRA-Match deposits, Match reversals) alongside
  // position rows. The user opts-in to applying them via this checkbox
  // — defaults ON since the Robinhood path implies the user wants the
  // full picture.
  const [applyCash, setApplyCash] = useState(true);
  const [skipDuplicates, setSkipDuplicates] = useState(true);  // Round 84h — auto-skip rows already in portfolio
  const [mapperOpen, setMapperOpen] = useState(false);
  const [csvTextCache, setCsvTextCache] = useState('');
  const [result, setResult] = useState(null);
  // Round 84k Phase 5.4 (Feb 15, 2026) — sticky banner that surfaces
  // import failures inside the modal so a fast-disappearing toast can
  // never hide a partial-import incident. Pairs with the backend
  // snapshot+rollback so we can guarantee "your data is safe" in the
  // copy.
  const [importError, setImportError] = useState(null);
  // Round P108v (Feb 2026) — per-user request, batch account_type is
  // NO LONGER pre-filled with the Taxable default. The user MUST pick
  // one on the preview step before Import is enabled, so nothing lands
  // as "Taxable/Untagged by omission" and later diverges from the tax
  // audit pages.
  const [acctTypeForBatch, setAcctTypeForBatch] = useState('');
  // Round P108k (Feb 2026) — bulk purchase-date assignment. When a
  // CSV or .xlsx doesn't carry a purchase-date column (or leaves it
  // blank for some rows), the backend defaults every row to today
  // during csv_preview. The chart line then flat-lines at $0 and
  // tax-lot age is wrong. This picker lets the user stamp a date
  // onto every row that has no purchase_date, in a single click,
  // BEFORE Continue → Import fires. Also editable per-row via the
  // inline Date input in the preview table below.
  const [bulkPurchaseDate, setBulkPurchaseDate] = useState('');
  // Round 92o++++++++N — optional broker tag for the whole batch.
  // '' = Untagged. Selected value is stamped on every imported row.
  const [brokerForBatch, setBrokerForBatch] = useState('');
  // Track whether the user successfully imported at least one row /
  // cash transaction during this open cycle. Drives the
  // "orphan-portfolio rollback" callback the parent (e.g. New
  // Portfolio modal) uses to delete an auto-created empty portfolio
  // when the user bails out before completing the import.
  const importCompletedRef = useRef(false);
  const fileInputRef = useRef(null);
  // Round P108t (Feb 2026) — stale-closure fix. handleXlsxConfirm's
  // setTimeout(handleParse, 50) captured the FIRST memoized
  // handleParse (which had a stale portfolioId), so imports could
  // fire against the wrong portfolio or silently no-op. Route the
  // deferred call through a ref that we keep pointed at the latest
  // handleParse via an effect after both are defined.
  const handleParseRef = useRef(null);
  // Round 95y — When the picked file is an .xlsx, we force the user
  // through an explicit "this will wipe your existing portfolio"
  // confirmation gate (because Excel/Google Sheets exports are
  // assumed to be a full snapshot, not a partial add). Tracked here
  // separately from the file picker so opening the modal afresh
  // resets the gate state too.
  const [xlsxConfirmOpen, setXlsxConfirmOpen] = useState(false);
  const [xlsxClearing, setXlsxClearing] = useState(false);
  // Round 95y — Live snapshot of what's about to be wiped, shown
  // inside the confirmation dialog so the user sees the actual
  // magnitude of the destruction. Fetched on dialog open, falls
  // back gracefully if the endpoint is slow or errors.
  const [xlsxSnapshot, setXlsxSnapshot] = useState(null);
  // Round P137 (Feb 2026) — Supported brokers list for the info
  // popover + per-broker sample-CSV download links. Fetched once from
  // /api/portfolio/csv-supported-brokers.
  const [supportedBrokers, setSupportedBrokers] = useState([]);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/portfolio/csv-supported-brokers`)
      .then(({ data }) => { if (!cancelled) setSupportedBrokers(data?.brokers || []); })
      .catch(() => { /* endpoint is best-effort; UI degrades gracefully */ });
    return () => { cancelled = true; };
  }, []);

  // Fetch the summary snapshot whenever the destructive confirmation
  // opens so the dialog shows real numbers (positions, value, cash)
  // instead of an abstract list of categories.
  //
  // Round P108o (Feb 2026) — SCOPE the fetch to the target portfolio.
  // Previously this hit /portfolio/summary user-wide, so the "N
  // positions worth $X" banner combined every portfolio the user
  // owned — misleading and terrifying when the user is uploading
  // into a brand-new empty portfolio that will only wipe itself.
  // Passing `portfolio_id` returns just the target's totals; brand-new
  // empty portfolios come back as `{position_count: 0, ...}` and the
  // dialog can honestly say "nothing to wipe — proceed with import".
  useEffect(() => {
    if (!xlsxConfirmOpen) {
      setXlsxSnapshot(null);
      return undefined;
    }
    let cancelled = false;
    axios
      .get(`${API}/portfolio/summary`, {
        params: portfolioId ? { portfolio_id: portfolioId } : {},
        withCredentials: true,
      })
      .then(({ data }) => {
        if (cancelled) return;
        setXlsxSnapshot({
          position_count: Number(data?.position_count || 0),
          positions_value: Number(data?.positions_value || 0),
          cash_balance: Number(data?.cash_balance || 0),
          annual_dividends: Number(data?.annual_dividends || 0),
        });
      })
      .catch(() => { if (!cancelled) setXlsxSnapshot({ error: true }); });
    return () => { cancelled = true; };
  }, [xlsxConfirmOpen, portfolioId]);

  // Reset every time the modal opens.
  useEffect(() => {
    if (open) {
      setStep('pick');
      setMode(null);
      setFile(null);
      setPreview(null);
      setAcceptedSuggestions({});
      setApplyCash(true);
      setResult(null);
      setImportError(null);
      importCompletedRef.current = false;
      setXlsxConfirmOpen(false);
      setXlsxClearing(false);
      setBulkPurchaseDate('');
    }
  }, [open]);

  // Round 92o++++++++N++++++++++ (Jun 2026) — Cancel button rework.
  // Old behaviour: Cancel always closed the whole modal, which (when
  // launched from the New-Portfolio flow) ALSO closed the parent and
  // left an empty orphaned portfolio behind. New rule:
  //   • If a file is staged but the user hasn't pressed Continue yet,
  //     Cancel clears just the file + parse-error banner (so the user
  //     can try a different CSV without losing the import context).
  //   • If no file is staged, Cancel closes the modal AND tells the
  //     parent (via onClosedWithoutImport) so it can delete the
  //     orphan portfolio it auto-created in step 1.
  const handleCancel = useCallback(() => {
    if (file) {
      // Clear just the file selection so the picker is ready again.
      setFile(null);
      setImportError(null);
      setMode(null);
      if (fileInputRef.current) {
        try { fileInputRef.current.value = ''; } catch { /* SSR / FF quirk */ }
      }
      return;
    }
    // No file staged — escape hatch. Close + signal rollback.
    if (!importCompletedRef.current && onClosedWithoutImport) {
      try { onClosedWithoutImport(); } catch { /* noop */ }
    }
    onOpenChange(false);
  }, [file, onClosedWithoutImport, onOpenChange]);

  const handleFile = (f) => {
    if (!f) return;
    const lower = f.name.toLowerCase();
    if (!lower.endsWith('.csv') && !lower.endsWith('.xlsx')) {
      toast.error('Only .csv or .xlsx files are accepted', {
        description: 'Either export your spreadsheet as CSV, or upload the DiviDrip .xlsx template directly.',
      });
      return;
    }
    setFile(f);
    // Round 95y — .xlsx uploads are full-snapshot overwrites. Fire the
    // destructive-action confirmation dialog the moment the file is
    // picked so the user makes the decision before parsing the file
    // or seeing any partial preview UI. .csv keeps the existing
    // Append vs Overwrite mode chooser unchanged.
    if (lower.endsWith('.xlsx')) {
      setXlsxConfirmOpen(true);
    }
  };

  // Round 95y — Run the same destructive reset that the "Reset
  // Portfolio" button at the bottom of the dashboard uses, then
  // automatically lock the mode to "overwrite" and kick off the parse.
  // Round P108d (Feb 2026) — CRITICAL data-loss fix. When launched
  // from the "+ New Portfolio" flow (Plus multi-portfolio), pass the
  // target `portfolio_id` so the pre-wipe is SCOPED to the new
  // (empty) portfolio only — never the user's other portfolios. Free
  // tier / Tool Box launcher leaves portfolioId null → legacy global
  // reset (matches the Dashboard's "Reset & Delete Portfolio"
  // button's intent).
  const handleXlsxConfirm = useCallback(async () => {
    setXlsxClearing(true);
    try {
      const { data: resetData } = await axios.post(
        `${API}/portfolio/reset-all`,
        portfolioId ? { portfolio_id: portfolioId } : {},
        { withCredentials: true },
      );
      // Round P108e — surface the Undo toast for the pre-wipe. If
      // the user changes their mind mid-import they can click Undo
      // within 15 minutes and get the target portfolio back exactly
      // as it was BEFORE the .xlsx wipe.
      if (resetData?.undo_snapshot_id) {
        showUndoToast(resetData, {
          successMessage: portfolioId
            ? `Cleared "${resetData.undo_portfolio_name || portfolioName || 'this portfolio'}" — importing your .xlsx now`
            : 'Cleared your portfolio — importing your .xlsx now',
        });
      } else {
        toast.success('Existing portfolio cleared. Importing your spreadsheet now...');
      }
      setMode('overwrite');
      setXlsxConfirmOpen(false);
      // Defer to next tick so the mode state lands before handleParse fires.
      // Route through the ref so we always call the LATEST memoized
      // handleParse (with fresh portfolioId), not the one captured
      // when handleXlsxConfirm was first created.
      setTimeout(() => { handleParseRef.current?.(); }, 50);
    } catch (e) {
      setXlsxClearing(false);
      toast.error('Could not clear the existing portfolio', {
        description: e?.response?.data?.detail || 'Try again in a moment.',
      });
    }
  }, [portfolioId, portfolioName]);

  const handleXlsxCancel = () => {
    setXlsxConfirmOpen(false);
    setFile(null);
    if (fileInputRef.current) {
      try { fileInputRef.current.value = ''; } catch { /* SSR quirk */ }
    }
  };

  const handleParse = useCallback(async () => {
    if (!file || !mode) return;
    setParseLoading(true);
    try {
      const isXlsx = file.name.toLowerCase().endsWith('.xlsx');
      let data;
      if (isXlsx) {
        // Round 95y — Direct .xlsx upload path. Sends the workbook as
        // multipart/form-data to /portfolio/xlsx-preview, which reads
        // the Holdings tab and delegates to the same csv-preview
        // logic so the rest of this modal works unchanged.
        const form = new FormData();
        form.append('file', file);
        if (portfolioId) form.append('portfolio_id', portfolioId);
        const r = await axios.post(`${API}/portfolio/xlsx-preview`, form, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        data = r.data;
        // Cache the rebuilt CSV so the Confirm step can use the existing
        // /portfolio/csv-import endpoint without re-uploading the xlsx.
        // The backend echoes the cleaned ticker / shares / cost / date
        // rows back as part of the preview response.
        if (Array.isArray(data?.rows) && data.rows.length > 0) {
          // Header keys must use spaces (not underscores) to match the
          // backend's _CSV_COST_KEYS / _CSV_DATE_KEYS mappings.
          const csvLines = ['ticker,shares,cost per share,purchase date'];
          data.rows.forEach((r) => {
            csvLines.push([
              r.ticker || '',
              r.shares ?? '',
              r.cost_per_share ?? '',
              r.purchase_date || '',
            ].join(','));
          });
          setCsvTextCache(csvLines.join('\n'));
        }
      } else {
        const csv_text = await file.text();
        setCsvTextCache(csv_text);
        const r = await axios.post(
          `${API}/portfolio/csv-preview`,
          // Round 92o++++++++N++++++++++ (May 2026) — Plus multi-
          // portfolio. Forward the target portfolio_id so the backend's
          // dupe-check is scoped to THAT portfolio (not user-wide).
          // Fixes the bug where uploading the same CSV into a new,
          // empty portfolio flagged every row as "already on file".
          { csv_text, portfolio_id: portfolioId || null },
          { withCredentials: true },
        );
        data = r.data;
      }
      setPreview(data);
      // Auto-accept suggestions by default when "show suggestions" is on
      const auto = {};
      data.rows.forEach((r) => { if (r.status === 'suggestion' && showSuggestions) auto[r.row_num] = true; });
      setAcceptedSuggestions(auto);
      setStep('preview');
    } catch (e) {
      toast.error('Could not read CSV', {
        description: e?.response?.data?.detail || 'Make sure the file has a Ticker and Shares column.',
      });
    } finally {
      setParseLoading(false);
    }
  }, [file, mode, showSuggestions, portfolioId]);

  // Keep the ref pointed at the latest handleParse so setTimeout
  // callbacks in handleXlsxConfirm never fire a stale closure.
  useEffect(() => {
    handleParseRef.current = handleParse;
  }, [handleParse]);

  const handleImport = useCallback(async () => {
    if (!preview || !mode) return;
    setImportLoading(true);
    // Build the row payload — drop invalid; for suggestions only include
    // the ones the user accepted (rewriting the ticker to suggested).
    // Round 84k Phase 5.5 (Feb 15, 2026) — "Skip duplicates" is
    // meaningless in overwrite mode because overwrite wipes the
    // existing portfolio first; no row CAN be a duplicate of an
    // about-to-be-deleted position. The checkbox is hidden from the
    // UI in overwrite mode (replaced with an explanation), but we
    // also force the filter off here so any state-stuck `true` value
    // from a prior append-mode session can't poison this import.
    const effectiveSkipDuplicates = mode === 'overwrite' ? false : skipDuplicates;
    const rows = preview.rows
      .filter((r) => r.status === 'valid' || (r.status === 'suggestion' && acceptedSuggestions[r.row_num]))
      .filter((r) => !(effectiveSkipDuplicates && r.conflict_existing))
      .map((r) => ({
        ticker: r.status === 'suggestion' ? r.suggested_ticker : r.ticker,
        shares: r.shares,
        cost_per_share: r.cost_per_share || r.current_price || 0,
        purchase_date: r.purchase_date || null,
      }));
    const cashRowsToSend = (preview.cash_rows && applyCash) ? preview.cash_rows : [];
    if (rows.length === 0 && cashRowsToSend.length === 0) {
      toast.error('Nothing to import', { description: 'No valid rows after filtering. Fix the CSV and try again.' });
      setImportLoading(false);
      return;
    }
    try {
      setImportError(null);
      const { data } = await axios.post(
        `${API}/portfolio/csv-import`,
        {
          rows,
          mode,
          cash_rows: cashRowsToSend,
          apply_cash: cashRowsToSend.length > 0,
          // P158·5 — Backend also dedupes cash transactions when this
          // is on (append mode). Previously the client-side filter only
          // dropped duplicate POSITION rows, so re-importing a Robinhood
          // CSV re-inserted every deposit / buy / sell / dividend and
          // inflated Total Contributions + Dividends Collected + drove
          // Net Capital Gain deeper negative.
          skip_duplicates: effectiveSkipDuplicates,
          account_type: acctTypeForBatch,
          broker: brokerForBatch || null,
          // Round P137 (Feb 2026) — forward the journal-draft rows the
          // preview surfaced (corrupt-date warnings, unknown broker
          // action codes, open short positions the FIFO schema can't
          // represent). Backend persists them as `jnl_starter`
          // journal_entries so the user can review them under
          // /journal → Drafts.
          jnl_rows: Array.isArray(preview?.jnl_rows) ? preview.jnl_rows : [],
          apply_jnl_starters: Array.isArray(preview?.jnl_rows) && preview.jnl_rows.length > 0,
          // Round 92o++++++++N++++++++++ (May 2026) — when the user
          // launched the CSV importer from the Create Portfolio flow,
          // every imported lot lands inside that newly-created group
          // instead of the legacy 'All' bucket. Null = unchanged
          // behaviour for the standalone Tool Box launcher.
          portfolio_id: portfolioId || null,
          // Round P118g — forward applied splits/spinoffs so the
          // backend persists an audit row for each, powering the
          // per-action Undo button in the header banner.
          applied_corp_actions: Array.isArray(preview?.skipped_corp_actions)
            ? preview.skipped_corp_actions.filter((ca) => ca?.applied)
            : [],
        },
        { withCredentials: true }
      );
      setResult(data);
      setStep('done');
      // Mark this open cycle as having a successful import so the
      // parent (New Portfolio flow) doesn't roll back the portfolio
      // we just populated. Set before the global event so listeners
      // can react if they want.
      importCompletedRef.current = true;
      window.dispatchEvent(new Event('portfolio-changed'));
      // Round P108e — for overwrite mode, the backend captured an
      // undo snapshot BEFORE wiping the target portfolio. Surface an
      // Undo toast so the user can revert with one click if the CSV
      // was wrong / imported to the wrong slot. Append mode is
      // non-destructive so no undo is offered.
      if (data?.undo_snapshot_id && mode === 'overwrite') {
        showUndoToast(data);
      }
      // Round P137 (Feb 2026) — Journal-drafts toast. When the parser
      // surfaced CSV_WARN / CSV_UNKNOWN / SHORT_OPEN rows, nudge the
      // user toward /journal → Drafts so they don't miss the review
      // step. Silent when everything imported cleanly.
      const jb = data?.jnl_breakdown;
      if (jb && jb.total > 0) {
        const parts = [];
        if (jb.CSV_WARN > 0) parts.push(`${jb.CSV_WARN} warning${jb.CSV_WARN === 1 ? '' : 's'}`);
        if (jb.CSV_UNKNOWN > 0) parts.push(`${jb.CSV_UNKNOWN} unknown ${jb.CSV_UNKNOWN === 1 ? 'type' : 'types'}`);
        if (jb.SHORT_OPEN > 0) parts.push(`${jb.SHORT_OPEN} open short${jb.SHORT_OPEN === 1 ? '' : 's'}`);
        if (jb.other > 0) parts.push(`${jb.other} other`);
        toast.info(`${jb.total} row${jb.total === 1 ? '' : 's'} landed in Journal drafts`, {
          description: `${parts.join(', ')} — review under /journal → Drafts.`,
          duration: 8000,
        });
      }
    } catch (e) {
      // Sticky banner instead of (just) a toast — the user reported a
      // case where the toast vanished too fast to read and they were
      // left wondering why nothing imported. Backend now does an
      // atomic snapshot+rollback on failure, so we can confidently
      // tell them their existing data is safe.
      const serverDetail = e?.response?.data?.detail;
      setImportError(
        serverDetail || 'Import failed. Your existing portfolio is unchanged — please try the same upload again.'
      );
      toast.error('Import failed', {
        description: serverDetail || 'See the banner above for details.',
      });
    } finally {
      setImportLoading(false);
    }
  }, [preview, mode, acceptedSuggestions, applyCash, skipDuplicates, acctTypeForBatch, brokerForBatch, portfolioId]);

  const handleViewPortfolio = () => {
    onOpenChange(false);
    // Scroll to top — same behaviour as the footer "Back to top" link.
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }, 50);
  };

  const stats = preview?.stats || { total: 0, valid: 0, invalid: 0, suggestions: 0 };
  const importableCount = stats.valid + (showSuggestions ? Object.values(acceptedSuggestions).filter(Boolean).length : 0);
  const cashCount = (preview?.cash_rows && applyCash) ? preview.cash_rows.length : 0;
  // Allow Confirm if there are positions to import OR cash transactions
  // queued — Robinhood files where every position has been sold off
  // can still be useful for importing the contribution history.
  // Round P108v (Feb 2026) — account_type + broker are now BOTH
  // required at the preview step. Blocks import until the user has
  // consciously picked each, so nothing lands as "Taxable/Untagged
  // by omission" (which the tax audit pages inherit).
  const missingAcctType = !acctTypeForBatch;
  const missingBroker = !brokerForBatch;
  const confirmDisabled = (importableCount === 0 && cashCount === 0) || importLoading || missingAcctType || missingBroker;

  // Round 92o++++++++N++++++++++ (May 2026) — Converted from Radix
  // Dialog to plain fixed-position modal (StockModal pattern).
  // Reason: the Radix Dialog's document-level pointerdown listener
  // treated browser-native scrollbar clicks (target = <html> or
  // <body>) as outside-clicks, slamming the modal closed when the
  // user tried to drag the preview table's horizontal scrollbar.
  // A plain modal hands scroll back to the browser cleanly and
  // doesn't intercept events it doesn't own. Internal Esc-to-close
  // is added below so keyboard UX matches the previous Radix
  // behavior.
  if (!open) return null;

  return (
    <>
    {/* Round 95y — Destructive-action confirmation for .xlsx uploads.
        Sits on top of the import modal so the user has to make the
        decision before any preview or import work happens. .xlsx is
        treated as a full snapshot — Append doesn't make sense, so we
        explicitly call /portfolio/reset-all on confirm. */}
    {xlsxConfirmOpen && (
      <div
        className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
        data-testid="xlsx-confirm-overlay"
        onClick={(e) => { if (e.target === e.currentTarget) handleXlsxCancel(); }}
      >
        <div
          className="relative w-full max-w-md bg-card border border-red-500/50 rounded-xl shadow-2xl p-6"
          data-testid="xlsx-confirm-dialog"
        >
          {/* Round P108o (Feb 2026) — target-scoped dialog. Three
              distinct states now, driven by the SCOPED /portfolio/summary
              snapshot (see xlsxSnapshot fetch above):
                1. Snapshot still loading → neutral spinner copy.
                2. Target portfolio is EMPTY (0 positions AND $0 cash)
                   → green "Ready to import — nothing to wipe" copy.
                   No red warnings, no misleading dollar figures. This
                   is the +New Portfolio → Upload .xlsx path.
                3. Target portfolio HAS data → the destructive warning
                   with the target-scoped magnitude box (values shown
                   are strictly for THIS portfolio, no sibling
                   portfolios / user-wide dollars leaking in). */}
          {(() => {
            const targetEmpty = xlsxSnapshot && !xlsxSnapshot.error
              && (xlsxSnapshot.position_count || 0) === 0
              && (xlsxSnapshot.cash_balance || 0) === 0;
            const targetHasData = xlsxSnapshot && !xlsxSnapshot.error && !targetEmpty;

            if (!xlsxSnapshot) {
              return (
                <>
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                    <h2 className="text-lg font-bold">Preparing import…</h2>
                  </div>
                  <div className="rounded-md border border-border bg-muted/30 px-3 py-2 mb-3 text-xs text-muted-foreground">
                    Checking &ldquo;{portfolioName || 'this portfolio'}&rdquo; for existing positions…
                  </div>
                </>
              );
            }

            if (targetEmpty) {
              return (
                <>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-emerald-500 text-xl leading-none">•</span>
                    <h2 className="text-lg font-bold text-emerald-700 dark:text-emerald-300">
                      Ready to import into &ldquo;{portfolioName || 'this portfolio'}&rdquo;
                    </h2>
                  </div>
                  <p className="text-sm text-foreground/90 mb-3">
                    This portfolio is <strong>empty</strong> — nothing to wipe. Your spreadsheet&rsquo;s positions will land here directly.
                  </p>
                  <p className="text-xs text-muted-foreground mb-4">
                    Your other portfolios, cash balance, watchlist, journal, alerts, share page, and DRIP settings are untouched.
                  </p>
                </>
              );
            }

            // targetHasData — legacy destructive-warning path, but now
            // strictly scoped to the target portfolio's numbers.
            return (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <h2 className="text-lg font-bold text-red-700 dark:text-red-300">
                    {portfolioId
                      ? `This will wipe "${portfolioName || 'this portfolio'}" only`
                      : 'This will wipe your existing portfolio'}
                  </h2>
                </div>
                <p className="text-sm text-foreground/90 mb-3">
                  <strong>.xlsx uploads completely overwrite</strong>{' '}
                  {portfolioId
                    ? <>the target portfolio&rsquo;s positions. Your other portfolios are <strong>not touched</strong>.</>
                    : <>your current portfolio. They do not append or merge with existing positions.</>
                  }
                </p>
                <p className="text-sm text-foreground/85 mb-3">
                  {portfolioId
                    ? <>The following will be permanently deleted <strong>from &ldquo;{portfolioName || 'this portfolio'}&rdquo; only</strong> before the spreadsheet is imported:</>
                    : <>All of the following will be permanently deleted before the spreadsheet is imported:</>
                  }
                </p>
                <div
                  className="rounded-md border border-red-300/40 dark:border-red-800/40 bg-red-50/60 dark:bg-red-950/30 px-3 py-2 mb-3 text-xs space-y-0.5"
                  data-testid="xlsx-confirm-magnitude"
                >
                  {targetHasData && xlsxSnapshot.position_count > 0 && (
                    <div>
                      <strong>{xlsxSnapshot.position_count}</strong>{' '}
                      {xlsxSnapshot.position_count === 1 ? 'position' : 'positions'} worth{' '}
                      <strong>${xlsxSnapshot.positions_value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                    </div>
                  )}
                  {targetHasData && xlsxSnapshot.cash_balance > 0 && !portfolioId && (
                    <div>
                      Cash balance:{' '}
                      <strong>${xlsxSnapshot.cash_balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                    </div>
                  )}
                  {targetHasData && (xlsxSnapshot.annual_dividends || 0) > 0 && (
                    <div>
                      Forward annual dividends:{' '}
                      <strong>${xlsxSnapshot.annual_dividends.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                    </div>
                  )}
                </div>
                <ul className="text-xs text-foreground/85 list-disc list-inside space-y-1 mb-4 ml-1">
                  <li>Every position and cost-basis lot{portfolioId ? ' in this portfolio' : ''}</li>
                  {portfolioId ? (
                    <li>This portfolio&rsquo;s CSV-imported deposits, buys, sells and dividend log</li>
                  ) : (
                    <li>Cash balance, deposits, withdrawals, and the full transaction log</li>
                  )}
                  {!portfolioId && <li>Dividends collected history</li>}
                  {!portfolioId && <li>Public share-page snapshot (if any) and DRIP overrides</li>}
                </ul>
                <p className="text-xs text-amber-700 dark:text-amber-300 mb-4">
                  {portfolioId
                    ? <>Your <strong>other portfolios, cash balance, watchlist, journal, alerts, share page, and DRIP settings are all preserved</strong>. To import incrementally without wiping this portfolio, cancel and use a <strong>.csv</strong> upload instead (Append mode supported).</>
                    : <>Watchlist, journal entries, alerts, and saved scenarios are kept. To import incrementally without wiping anything, cancel and use a <strong>.csv</strong> upload instead (Append mode supported).</>
                  }
                </p>
              </>
            );
          })()}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleXlsxCancel}
              disabled={xlsxClearing}
              data-testid="xlsx-confirm-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant={(xlsxSnapshot && !xlsxSnapshot.error && (xlsxSnapshot.position_count || 0) === 0 && (xlsxSnapshot.cash_balance || 0) === 0) ? 'default' : 'destructive'}
              onClick={handleXlsxConfirm}
              disabled={xlsxClearing}
              data-testid="xlsx-confirm-ok-btn"
            >
              {xlsxClearing
                ? 'Importing…'
                : (xlsxSnapshot && !xlsxSnapshot.error && (xlsxSnapshot.position_count || 0) === 0 && (xlsxSnapshot.cash_balance || 0) === 0)
                  ? 'OK — import'
                  : 'OK — clear & import'}
            </Button>
          </div>
        </div>
      </div>
    )}
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-2 sm:p-4"
      data-testid="csv-import-overlay"
      onClick={(e) => {
        // Only the backdrop itself closes — bubbled clicks (scroll-
        // bar interactions, inner form clicks, etc.) MUST NOT close
        // the modal. Also route through the orphan-rollback path so
        // an auto-created empty portfolio doesn't linger if the user
        // dismisses the importer without finishing.
        if (e.target === e.currentTarget) {
          if (!importCompletedRef.current && onClosedWithoutImport) {
            try { onClosedWithoutImport(); } catch { /* noop */ }
          }
          onOpenChange(false);
        }
      }}
    >
      <div
        className="relative w-full max-w-2xl max-h-[92vh] bg-card border rounded-xl shadow-2xl flex flex-col overflow-hidden"
        data-testid="csv-import-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => {
            if (!importCompletedRef.current && onClosedWithoutImport) {
              try { onClosedWithoutImport(); } catch { /* noop */ }
            }
            onOpenChange(false);
          }}
          className="absolute top-3 right-3 z-10 p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
          aria-label="Close"
          data-testid="csv-import-close-x"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="overflow-y-auto px-4 sm:px-6 py-4 sm:py-5 space-y-4">
        <div className="space-y-1.5 pr-8">
          <h2 className="text-lg font-bold flex items-center gap-2" data-testid="csv-import-title">
            <FileSpreadsheet className="w-5 h-5 text-emerald-500" />
            Import Portfolio from CSV
          </h2>
        </div>

        {/* Round 92o++++++++N++++++++++ (May 2026) — when launched
            from the Create Portfolio flow, banner the imported-into
            destination so the user knows where these rows are going.
            Free-tier launch (Tool Box) leaves portfolioId null and
            this banner stays hidden. */}
        {portfolioId && (
          <div
            className="rounded-md border border-violet-300 dark:border-violet-700 bg-violet-50/60 dark:bg-violet-950/40 px-3 py-2 text-xs"
            data-testid="csv-import-portfolio-banner"
          >
            <span className="font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">Importing into:</span>{' '}
            <span className="font-semibold">{portfolioName || 'New portfolio'}</span>
          </div>
        )}

        {/* STEP 1 — Pick file + choose mode */}
        {step === 'pick' && (
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Upload a CSV from your broker, a previous DiviDrip export, or any spreadsheet that has at minimum
              a <span className="font-mono text-xs">Ticker</span> and <span className="font-mono text-xs">Shares</span> column.
              <span className="block mt-1 text-xs">
                <span className="font-semibold text-amber-600 dark:text-amber-400">Robinhood account-activity exports are auto-detected</span>
                {' '}— Buys, Sells, DRIP reinvestments, IRA contributions, IRA Match deposits, and Match reversals are all parsed automatically.
              </span>
            </p>

            {/* Mode selection — REQUIRED */}
            <div className="rounded-lg border border-border bg-muted/30 px-4 py-3" data-testid="csv-mode-section">
              <p className="text-xs font-bold uppercase tracking-wider text-foreground mb-2">
                Choose how to apply this import <span className="text-rose-500">*</span>
              </p>
              <label
                className={`flex items-start gap-2 px-3 py-2 rounded cursor-pointer transition-colors ${
                  mode === 'append' ? 'bg-emerald-100 dark:bg-emerald-950/60 ring-1 ring-emerald-400/60' : 'hover:bg-accent/40'
                }`}
                data-testid="csv-mode-append"
              >
                <input
                  type="radio"
                  name="csv-mode"
                  value="append"
                  checked={mode === 'append'}
                  onChange={() => setMode('append')}
                  className="mt-0.5"
                  data-testid="csv-mode-append-radio"
                />
                <span className="text-sm">
                  <span className="font-bold">Add to Existing Portfolio</span>
                  <span className="block text-xs text-muted-foreground mt-0.5">
                    Imported positions are added on top of what you already own. Same ticker can appear as multiple cost-basis lots.
                  </span>
                </span>
              </label>
              <label
                className={`flex items-start gap-2 px-3 py-2 rounded cursor-pointer transition-colors mt-1 ${
                  mode === 'overwrite' ? 'bg-rose-100 dark:bg-rose-950/60 ring-1 ring-rose-400/60' : 'hover:bg-accent/40'
                }`}
                data-testid="csv-mode-overwrite"
              >
                <input
                  type="radio"
                  name="csv-mode"
                  value="overwrite"
                  checked={mode === 'overwrite'}
                  onChange={() => setMode('overwrite')}
                  className="mt-0.5"
                  data-testid="csv-mode-overwrite-radio"
                />
                <span className="text-sm">
                  <span className="font-bold text-rose-700 dark:text-rose-300">Overwrite Existing Portfolio</span>
                  <span className="block text-xs text-muted-foreground mt-0.5">
                    All current positions are deleted before the import runs. <span className="font-semibold text-rose-600 dark:text-rose-400">This cannot be undone.</span>
                    {' '}All prior cash transactions for this portfolio (deposits, buys, sells, DRIPs, dividends) are reversed so cash balance rebuilds cleanly from the new CSV. Watchlist and income goals are kept.
                  </span>
                </span>
              </label>
            </div>

            {/* File picker */}
            <div className="rounded-lg border-2 border-dashed border-border bg-card px-4 py-6 text-center">
              <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => handleFile(e.target.files?.[0])}
                className="hidden"
                data-testid="csv-file-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                data-testid="csv-pick-file-btn"
              >
                {file ? `Change file (${file.name})` : 'Choose .csv or .xlsx file'}
              </Button>
              {file && (
                <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-2 font-mono" data-testid="csv-selected-filename">
                  {file.name} · {(file.size / 1024).toFixed(1)} KB
                </p>
              )}
            </div>

            <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer" data-testid="csv-show-suggestions-row">
              <input
                type="checkbox"
                checked={showSuggestions}
                onChange={(e) => setShowSuggestions(e.target.checked)}
                data-testid="csv-show-suggestions-checkbox"
              />
              <Lightbulb className="w-3 h-3 text-amber-500" />
              Show fuzzy-match suggestions for unknown tickers (e.g. <span className="font-mono">BRKB → BRK.B</span>)
            </label>

            <a
              href={`${API}/portfolio/csv-sample`}
              className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-cyan-400 hover:underline"
              data-testid="csv-download-sample"
            >
              <Download className="w-3 h-3" />
              Download the DiviDrip sample CSV
            </a>

            {/* Round P137 (Feb 2026) — Supported Brokers info popover.
                Confirms coverage BEFORE the user tries to upload, and
                gives every broker a one-click sample template so they
                can round-trip a test import first. */}
            <details className="rounded-lg border border-border/50 bg-muted/20 open:bg-muted/30" data-testid="csv-supported-brokers">
              <summary className="cursor-pointer text-xs font-semibold text-foreground/80 hover:text-foreground px-3 py-2 flex items-center gap-2 select-none">
                <Info className="w-3.5 h-3.5 text-blue-600 dark:text-cyan-400" />
                Supported brokers
                {supportedBrokers.length > 0 && (
                  <span className="ml-1 text-[10px] font-mono text-muted-foreground">
                    ({supportedBrokers.length})
                  </span>
                )}
                <span className="ml-auto text-[10px] font-normal text-muted-foreground">
                  Native CSV/XLSX exports auto-detected
                </span>
              </summary>
              <div className="border-t border-border/40 px-3 py-2">
                <p className="text-[11px] text-muted-foreground leading-snug mb-2">
                  Upload your broker&rsquo;s exported transaction history as-is — DiviDrip auto-detects the format and parses buys, sells, DRIPs, dividends, splits, and short positions. Grab a sample template to test the flow before importing your real data.
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1" data-testid="csv-supported-brokers-list">
                  {supportedBrokers.map((br) => (
                    <li key={br.key} className="flex items-center justify-between gap-2 text-[11px]">
                      <span className="text-foreground/90 truncate">{br.label}</span>
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}${br.sample_url}`}
                        className="inline-flex items-center gap-0.5 text-blue-600 dark:text-cyan-400 hover:underline shrink-0"
                        data-testid={`csv-sample-${br.key}`}
                      >
                        <Download className="w-3 h-3" />
                        <span className="text-[10px]">Sample</span>
                      </a>
                    </li>
                  ))}
                </ul>
                <p className="text-[10px] text-muted-foreground mt-2 leading-snug">
                  Broker not listed? Use the manual column mapper below or the DiviDrip template — most spreadsheet exports with Ticker / Shares / Cost / Date columns import cleanly.
                </p>
              </div>
            </details>

            {/* Round 84h — explicit column mapper for non-Robinhood / non-native brokerages */}
            <button
              type="button"
              onClick={async () => {
                if (!file) {
                  toast.error('Pick a CSV file first');
                  return;
                }
                if (file.name.toLowerCase().endsWith('.xlsx')) {
                  toast.error('.xlsx files use the DiviDrip template\'s known columns', {
                    description: 'The column mapper is only for non-standard .csv files. Click "Parse File" below to import the .xlsx directly.',
                  });
                  return;
                }
                const text = await file.text();
                setCsvTextCache(text);
                setMapperOpen(true);
              }}
              className="inline-flex items-center gap-1 text-xs text-violet-600 dark:text-violet-400 hover:underline disabled:opacity-50"
              disabled={!file}
              data-testid="csv-open-mapper-btn"
            >
              <Wand2 className="w-3 h-3" />
              Different brokerage? Map columns manually
            </button>
          </div>
        )}

        {/* STEP 2 — Preview */}
        {step === 'preview' && preview && (
          <div className="space-y-3 py-2">
            {importError && (
              <div
                className="rounded-lg border-2 border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-950/30 p-3 flex items-start gap-2"
                data-testid="csv-import-error-banner"
              >
                <AlertTriangle className="w-5 h-5 text-rose-600 dark:text-rose-400 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-rose-800 dark:text-rose-200 mb-1">Import failed — your data is safe</p>
                  <p className="text-xs text-rose-800 dark:text-rose-300 leading-snug">{importError}</p>
                  <p className="text-[11px] text-rose-700 dark:text-rose-400 mt-1.5">
                    Click <strong>Import</strong> again with the same file — transient backend hiccups usually clear on the second attempt.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setImportError(null)}
                  className="text-rose-600 dark:text-rose-400 hover:text-rose-800 dark:hover:text-rose-200 p-1"
                  aria-label="Dismiss"
                  data-testid="csv-import-error-dismiss"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            <div
              className="grid grid-cols-3 gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2 text-center"
              data-testid="csv-preview-stats"
            >
              <div>
                <p className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Valid</p>
                <p className="text-lg font-mono font-bold text-emerald-600 dark:text-emerald-400" data-testid="csv-stats-valid">{stats.valid}</p>
              </div>
              <div>
                <p className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Suggestions</p>
                <p className="text-lg font-mono font-bold text-amber-600 dark:text-amber-400" data-testid="csv-stats-suggestions">{stats.suggestions}</p>
              </div>
              <div>
                <p className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Invalid</p>
                <p className="text-lg font-mono font-bold text-rose-600 dark:text-rose-400" data-testid="csv-stats-invalid">{stats.invalid}</p>
              </div>
            </div>
            {preview.detected_format === 'native' && (
              <div className="flex items-center gap-2 text-xs text-emerald-600 dark:text-emerald-400 px-1" data-testid="csv-detected-native">
                <Sparkles className="w-3 h-3" />
                Detected DiviDrip native export — column shape preserved
              </div>
            )}
            {preview.detected_format === 'robinhood' && (
              <div className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-400 px-1" data-testid="csv-detected-robinhood">
                <Sparkles className="w-3 h-3" />
                Detected Robinhood account-activity CSV — Buy / Sell / DRIP / Cash transactions parsed automatically
              </div>
            )}
            {preview.detected_format === 'mapped' && (
              <div className="flex items-center gap-2 text-xs text-violet-600 dark:text-violet-400 px-1" data-testid="csv-detected-mapped">
                <Wand2 className="w-3 h-3" />
                Custom column mapping applied
              </div>
            )}
            {/* Round 84h — duplicate-position warning. Surfaces the DRIP
                double-count scenario before commit. */}
            {Number(stats.duplicates_in_portfolio || 0) > 0 && (
              <div
                className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50/60 dark:bg-amber-950/30 p-3"
                data-testid="csv-duplicate-warning"
              >
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs font-bold text-amber-800 dark:text-amber-300">
                      {stats.duplicates_in_portfolio} {stats.duplicates_in_portfolio === 1 ? 'ticker is' : 'tickers are'} already in your portfolio
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
                      These rows are flagged below. Skipping them is recommended to avoid double-counting (e.g. DRIP-purchased shares already on file).
                    </p>
                    {mode === 'overwrite' ? (
                      <p
                        className="mt-2 text-[11px] font-semibold text-emerald-700 dark:text-emerald-400 flex items-center gap-1.5"
                        data-testid="csv-skip-duplicates-na"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        Not applicable — Overwrite mode wipes existing positions first, so every row will be imported fresh.
                      </p>
                    ) : (
                      <label className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold cursor-pointer" data-testid="csv-skip-duplicates-row">
                        <input
                          type="checkbox"
                          checked={skipDuplicates}
                          onChange={(e) => setSkipDuplicates(e.target.checked)}
                          className="w-3.5 h-3.5 accent-amber-600"
                          data-testid="csv-skip-duplicates-checkbox"
                        />
                        Skip duplicates on import
                      </label>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Cash transactions section — only Robinhood imports
                populate this. Lets the user opt-in to applying IRA
                contributions (CFIR), IRA-Match deposits (MTCH), and
                Match reversals (parens-amount MTCH) alongside the
                position rows. CDIV entries are not surfaced because
                Robinhood pairs them with an immediate DRIP Buy of the
                same dollar amount — applying both would double-count. */}
            {preview.cash_rows && preview.cash_rows.length > 0 && (
              <div className="rounded-lg border border-amber-300/60 dark:border-amber-700/50 bg-amber-50/50 dark:bg-amber-950/20 p-3" data-testid="csv-cash-section">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={applyCash}
                    onChange={(e) => setApplyCash(e.target.checked)}
                    className="mt-1"
                    data-testid="csv-apply-cash-checkbox"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-sm font-bold text-amber-800 dark:text-amber-200">
                      <Wallet className="w-4 h-4" />
                      Also import {preview.cash_rows.length} cash transaction{preview.cash_rows.length === 1 ? '' : 's'}
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      <span className="font-mono font-semibold text-emerald-700 dark:text-emerald-400">{preview.cash_summary?.deposits || 0}</span> deposits
                      {preview.cash_summary?.buys > 0 && (
                        <>
                          {' · '}
                          <span className="font-mono font-semibold text-rose-700 dark:text-rose-400">{preview.cash_summary.buys}</span> buys
                        </>
                      )}
                      {preview.cash_summary?.sells > 0 && (
                        <>
                          {' · '}
                          <span className="font-mono font-semibold text-emerald-700 dark:text-emerald-400">{preview.cash_summary.sells}</span> sells
                        </>
                      )}
                      {preview.cash_summary?.removes > 0 && (
                        <>
                          {' · '}
                          <span className="font-mono font-semibold text-rose-700 dark:text-rose-400">{preview.cash_summary.removes}</span> match reversals
                        </>
                      )}
                      {' · net '}
                      <span className="font-mono font-bold text-foreground">${(preview.cash_summary?.net_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      {' applied to your cash balance.'}
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      IRA contributions (CFIR) and IRA-Match deposits (MTCH) become outside-cash deposits. Match reversals are removed without counting as withdrawals. Buy / Sell rows debit / credit your inside cash so cash balance after import matches your broker&rsquo;s &ldquo;Buying Power&rdquo;.
                    </p>
                    {/* Feb 2026 (Round P111g) — Import Preview Diff.
                        Show the financial totals the aggregator will
                        compute AFTER commit, so surprises like the
                        $270 auto-plug never land unseen again. */}
                    {(preview.cash_summary?.dividends > 0
                      || preview.cash_summary?.sells > 0
                      || preview.cash_summary?.auto_plug_would_fire) && (
                      <div
                        className="mt-2 rounded border border-amber-300/60 dark:border-amber-800/40 bg-amber-50/70 dark:bg-amber-950/20 p-2 text-[11px] font-mono grid grid-cols-2 gap-x-3 gap-y-1"
                        data-testid="csv-preview-financial-diff"
                      >
                        <div className="col-span-2 text-[10px] font-sans font-semibold text-amber-900 dark:text-amber-100 uppercase tracking-wide mb-0.5">
                          After Import
                        </div>
                        <div className="text-muted-foreground">Total Contributions</div>
                        <div className="text-right font-bold text-foreground" data-testid="csv-preview-contributions">
                          ${((preview.cash_summary?.total_deposits || 0) - (preview.cash_summary?.total_outside_removes || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                        <div className="text-muted-foreground">Dividends Collected</div>
                        <div className="text-right font-bold text-emerald-700 dark:text-emerald-400" data-testid="csv-preview-dividends">
                          ${(preview.cash_summary?.total_dividends || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                        <div className="text-muted-foreground">Realized G/L</div>
                        <div
                          className={`text-right font-bold ${(preview.cash_summary?.total_realized_gain_loss || 0) < 0 ? 'text-rose-700 dark:text-rose-400' : 'text-emerald-700 dark:text-emerald-400'}`}
                          data-testid="csv-preview-realized-gl"
                        >
                          {(preview.cash_summary?.total_realized_gain_loss || 0) < 0 ? '−' : '+'}${Math.abs(preview.cash_summary?.total_realized_gain_loss || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                        {preview.cash_summary?.auto_plug_would_fire && (
                          <>
                            <div className="col-span-2 text-[10px] font-sans text-amber-800 dark:text-amber-300 mt-1">
                              ⚠ Auto cost-basis plug: <span className="font-mono font-bold">${(preview.cash_summary?.auto_plug_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              <span className="block text-[10px] text-muted-foreground">
                                Positions-only CSV — synthetic deposit will be added to make Net Capital Gain math accurate.
                                Doesn&apos;t affect cash balance.
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                    <details className="mt-2 text-[11px]">
                      <summary className="cursor-pointer text-muted-foreground hover:text-foreground select-none">
                        Show all {preview.cash_rows.length} cash transactions
                      </summary>
                      <div className="max-h-44 overflow-y-auto mt-1 rounded border border-amber-200/60 dark:border-amber-800/40 bg-card/50">
                        <table className="w-full text-[11px] font-mono">
                          <thead className="bg-amber-100/60 dark:bg-amber-950/40 sticky top-0">
                            <tr>
                              <th className="px-2 py-1 text-left">Date</th>
                              <th className="px-2 py-1 text-left">Code</th>
                              <th className="px-2 py-1 text-left">Type</th>
                              <th className="px-2 py-1 text-right">Amount</th>
                              <th className="px-2 py-1 text-left">Note</th>
                            </tr>
                          </thead>
                          <tbody>
                            {preview.cash_rows.map((c, i) => {
                              const isCredit = c.type === 'deposit' || c.type === 'sell';
                              const label = (
                                c.type === 'deposit' ? '+ deposit' :
                                c.type === 'sell'    ? '+ sell' :
                                c.type === 'buy'     ? '− buy' :
                                c.type === 'withdraw' ? '− withdraw' :
                                '− remove'
                              );
                              return (
                                <tr key={i} className="odd:bg-amber-50/40 dark:odd:bg-amber-950/20" data-testid={`csv-cash-row-${i}`}>
                                  <td className="px-2 py-1">{c.date || '—'}</td>
                                  <td className="px-2 py-1 font-bold">{c.trans_code || '—'}</td>
                                  <td className={`px-2 py-1 font-bold ${isCredit ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400'}`}>
                                    {label}
                                  </td>
                                  <td className="px-2 py-1 text-right">${Number(c.amount || 0).toFixed(2)}</td>
                                  <td className="px-2 py-1 text-muted-foreground truncate max-w-[200px]" title={c.note}>{c.note || ''}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </details>
                  </div>
                </label>
              </div>
            )}

            <div className="max-h-72 overflow-y-auto rounded-lg border border-border" data-testid="csv-preview-table-wrap">
              {/* Round P108k (Feb 2026) — Bulk purchase-date assign banner.
                  Only surfaced when at least one row arrived without a
                  date (backend flagged them `date_was_missing: true`).
                  A single date picker at the top stamps every flagged
                  row in one click; the inline Date input in each row
                  still allows per-row precision. Silent no-op when
                  every row already has a real date. */}
              {(() => {
                const missingCount = (preview?.rows || []).filter(r => r.date_was_missing).length;
                if (missingCount === 0) return null;
                const applyBulk = () => {
                  if (!bulkPurchaseDate) return;
                  setPreview(prev => prev ? ({
                    ...prev,
                    rows: prev.rows.map(r => r.date_was_missing
                      ? { ...r, purchase_date: bulkPurchaseDate, date_was_missing: false, message: r.message === 'Purchase date missing — used today' ? null : r.message }
                      : r
                    ),
                  }) : prev);
                  toast.success(`Applied ${bulkPurchaseDate} to ${missingCount} row${missingCount === 1 ? '' : 's'}`);
                  setBulkPurchaseDate('');
                };
                return (
                  <div
                    className="border-b border-amber-400/40 bg-amber-50 dark:bg-amber-950/25 px-3 py-2 flex flex-col sm:flex-row sm:items-center gap-2"
                    data-testid="csv-bulk-date-banner"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-amber-800 dark:text-amber-300">
                        {missingCount} row{missingCount === 1 ? '' : 's'} arrived without a purchase date
                      </p>
                      <p className="text-[11px] text-amber-700 dark:text-amber-400 leading-snug">
                        Currently defaulting to today. Pick a real date and click Apply, or set dates row-by-row below.
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <input
                        type="date"
                        value={bulkPurchaseDate}
                        onChange={(e) => setBulkPurchaseDate(e.target.value)}
                        max={new Date().toISOString().slice(0, 10)}
                        className="px-2 py-1 rounded-md border border-amber-400/60 bg-background text-xs font-mono focus:outline-none focus:ring-2 focus:ring-amber-500/40"
                        data-testid="csv-bulk-date-input"
                        aria-label="Bulk purchase date"
                      />
                      <button
                        type="button"
                        onClick={applyBulk}
                        disabled={!bulkPurchaseDate}
                        className="px-3 py-1 rounded-md text-xs font-semibold bg-amber-600 hover:bg-amber-700 disabled:bg-amber-300 disabled:cursor-not-allowed text-white transition-colors"
                        data-testid="csv-bulk-date-apply"
                      >
                        Apply to all {missingCount}
                      </button>
                    </div>
                  </div>
                );
              })()}
              {/* Round P118f (Feb 2026) — Split & spinoff surface.
                  The parser auto-applies stock splits to pre-split
                  lots (halves cost + doubles shares for a 2-for-1)
                  and (P118g) auto-allocates spinoffs when FMVs are
                  provided in the Notes column. Every applied action
                  gets recorded in `applied_corp_actions` — after
                  Confirm, a per-row Undo is available via the
                  header's Portfolio Settings dropdown ("Recent
                  Corp Actions"). */}
              {Array.isArray(preview?.skipped_corp_actions) && preview.skipped_corp_actions.length > 0 && (
                <div
                  className="mt-2 mb-3 rounded-md border border-violet-400/50 bg-violet-500/10 p-2.5 text-xs"
                  data-testid="csv-preview-corp-actions"
                >
                  <div className="font-semibold text-violet-800 dark:text-violet-200 mb-1.5 flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5" />
                    Corporate actions detected ({preview.skipped_corp_actions.length})
                    {preview.skipped_corp_actions.some((ca) => ca.applied) && (
                      <span className="ml-auto text-[9.5px] font-normal text-violet-800/70 dark:text-violet-200/70">
                        Undo available after import via Portfolio Settings › Recent Corp Actions
                      </span>
                    )}
                  </div>
                  <ul className="space-y-1 text-[11px] text-violet-900/90 dark:text-violet-100/90">
                    {preview.skipped_corp_actions.map((ca, i) => (
                      <li key={i} className="font-mono">
                        <span className={`inline-block px-1 rounded mr-1.5 text-[9px] font-bold uppercase tracking-wide ${
                          ca.applied
                            ? 'bg-emerald-500/25 text-emerald-800 dark:text-emerald-200'
                            : 'bg-amber-500/25 text-amber-800 dark:text-amber-200'
                        }`}>
                          {ca.applied ? '✓ Applied' : '⚠ Skipped'}
                        </span>
                        {ca.date ? `${ca.date} · ` : ''}
                        {ca.parent_ticker ? `${ca.parent_ticker} → ` : ''}
                        {ca.ticker ? `${ca.ticker} ` : ''}
                        {ca.type}
                        {ca.ratio ? ` (ratio ${ca.ratio}×)` : ''}
                        <span className="block ml-24 text-[10px] text-violet-800/70 dark:text-violet-200/70 font-sans">
                          {ca.reason}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <table className="w-full text-xs">
                <thead className="bg-muted/40 sticky top-0">
                  <tr>
                    <th className="px-2 py-1.5 text-left font-semibold">#</th>
                    <th className="px-2 py-1.5 text-left font-semibold">Ticker</th>
                    <th className="px-2 py-1.5 text-right font-semibold">Shares</th>
                    <th className="px-2 py-1.5 text-right font-semibold">Cost</th>
                    <th className="px-2 py-1.5 text-left font-semibold">Date</th>
                    <th className="px-2 py-1.5 text-left font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => {
                    const cls =
                      r.status === 'valid'
                        ? 'border-l-2 border-emerald-500'
                        : r.status === 'suggestion'
                        ? 'border-l-2 border-amber-500'
                        : 'border-l-2 border-rose-500';
                    const Icon =
                      r.status === 'valid' ? CheckCircle2 : r.status === 'suggestion' ? Lightbulb : XCircle;
                    const iconCls =
                      r.status === 'valid'
                        ? 'text-emerald-500'
                        : r.status === 'suggestion'
                        ? 'text-amber-500'
                        : 'text-rose-500';
                    const accepted = !!acceptedSuggestions[r.row_num];
                    return (
                      <tr
                        key={r.row_num}
                        className={`${cls} odd:bg-muted/10 align-top`}
                        data-testid={`csv-preview-row-${r.row_num}`}
                      >
                        <td className="px-2 py-1.5 font-mono text-muted-foreground">{r.row_num}</td>
                        <td className="px-2 py-1.5 font-mono font-bold">
                          <span className="inline-flex items-center gap-1.5">
                            {r.ticker || '—'}
                            {/* Round P118d — DRIP badge. Preview
                                payload now carries per-row `is_drip`
                                so the user can see WHICH lots will
                                be marked as reinvestments BEFORE
                                confirming the import. Uses a cyan
                                water-drop pill matching the badge
                                the StockBar renders on the live
                                portfolio. */}
                            {r.is_drip && (
                              <span
                                className="inline-flex items-center gap-0.5 rounded-full bg-cyan-500/15 px-1.5 py-0.5 text-[8.5px] font-bold uppercase tracking-wide text-cyan-700 dark:text-cyan-300 border border-cyan-500/40"
                                title="Dividend reinvestment (DRIP). Water-drop badge will appear on the live portfolio lot."
                                data-testid={`csv-preview-drip-badge-${r.row_num}`}
                              >
                                <Droplet className="w-2.5 h-2.5" strokeWidth={2.5} />
                                DRIP
                              </span>
                            )}
                          </span>
                          {r.suggested_ticker && (
                            <span className="block text-[10px] font-normal text-amber-700 dark:text-amber-400">
                              → {r.suggested_ticker}
                            </span>
                          )}
                          {r.conflict_existing && (
                            <span
                              className="block text-[9px] font-bold uppercase tracking-wide text-amber-700 dark:text-amber-400 mt-0.5"
                              data-testid={`csv-conflict-${r.row_num}`}
                              title="This ticker is already in your portfolio."
                            >
                              ⚠ already on file
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono">{r.shares || '—'}</td>
                        <td className="px-2 py-1.5 text-right font-mono">
                          {r.cost_per_share ? `$${Number(r.cost_per_share).toFixed(2)}` : '—'}
                        </td>
                        <td className="px-2 py-1.5 font-mono">
                          {/* Round P108k — inline per-row date editor
                              for rows that arrived without a date.
                              Regular (non-flagged) rows keep the
                              read-only display so we don't distract
                              users whose data was already correct. */}
                          {r.date_was_missing ? (
                            <input
                              type="date"
                              value={r.purchase_date || ''}
                              max={new Date().toISOString().slice(0, 10)}
                              onChange={(e) => {
                                const v = e.target.value;
                                setPreview(prev => prev ? ({
                                  ...prev,
                                  rows: prev.rows.map(row => row.row_num === r.row_num
                                    ? { ...row, purchase_date: v, date_was_missing: v ? false : row.date_was_missing }
                                    : row
                                  ),
                                }) : prev);
                              }}
                              className="px-1.5 py-0.5 rounded border border-amber-400/60 bg-amber-50/50 dark:bg-amber-950/20 text-xs font-mono w-[130px] focus:outline-none focus:ring-1 focus:ring-amber-500/40"
                              data-testid={`csv-row-date-${r.row_num}`}
                              aria-label={`Purchase date for row ${r.row_num}`}
                            />
                          ) : (
                            r.purchase_date || '—'
                          )}
                        </td>
                        <td className="px-2 py-1.5">
                          <div className="flex items-start gap-1">
                            <Icon className={`w-3.5 h-3.5 ${iconCls} shrink-0 mt-0.5`} />
                            <div>
                              <span className="capitalize text-[11px] font-semibold">{r.status}</span>
                              {r.message && (
                                <span className="block text-[10px] text-muted-foreground leading-tight">
                                  {r.message}
                                </span>
                              )}
                              {r.status === 'suggestion' && showSuggestions && (
                                <label className="inline-flex items-center gap-1 mt-0.5 text-[10px] cursor-pointer text-amber-700 dark:text-amber-400">
                                  <input
                                    type="checkbox"
                                    checked={accepted}
                                    onChange={(e) =>
                                      setAcceptedSuggestions((prev) => ({ ...prev, [r.row_num]: e.target.checked }))
                                    }
                                    data-testid={`csv-accept-suggestion-${r.row_num}`}
                                  />
                                  Use {r.suggested_ticker}
                                </label>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {mode === 'overwrite' && (
              <div className="flex items-start gap-2 rounded-lg border border-rose-300/70 dark:border-rose-700/60 bg-rose-50 dark:bg-rose-950/30 px-3 py-2 text-xs text-rose-800 dark:text-rose-200" data-testid="csv-overwrite-warning">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>
                  Overwrite mode is <strong>destructive</strong>. All your current positions will be deleted before this import runs.
                </span>
              </div>
            )}
          </div>
        )}

        {/* STEP 3 — Done */}
        {step === 'done' && result && (
          <div className="space-y-3 py-2 text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-950/60 flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-emerald-500" />
            </div>
            <h3 className="text-lg font-bold" data-testid="csv-success-title">Import Complete</h3>
            <p className="text-sm text-muted-foreground" data-testid="csv-success-summary">
              Imported{' '}
              <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400">{result.imported}</span> {result.imported === 1 ? 'position' : 'positions'}
              {result.skipped > 0 && (
                <>
                  , skipped{' '}
                  <span className="font-mono font-bold text-rose-600 dark:text-rose-400">{result.skipped}</span> invalid {result.skipped === 1 ? 'row' : 'rows'}
                </>
              )}
              {result.deleted_existing > 0 && (
                <>
                  {' '}(replaced{' '}
                  <span className="font-mono font-bold">{result.deleted_existing}</span> previous {result.deleted_existing === 1 ? 'position' : 'positions'})
                </>
              )}
              .
            </p>
            {result.cash_imported > 0 && (
              <p className="text-sm text-muted-foreground" data-testid="csv-success-cash-summary">
                <Wallet className="w-3.5 h-3.5 inline mr-1 text-amber-500" />
                Plus{' '}
                <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400">{result.cash_imported}</span> cash {result.cash_imported === 1 ? 'transaction' : 'transactions'}
                {' '}(net{' '}
                <span className="font-mono font-bold text-foreground">${(((result.cash_total_deposit || 0) + (result.cash_total_sell || 0)) - ((result.cash_total_remove || 0) + (result.cash_total_buy || 0))).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                {' '}to cash balance).
              </p>
            )}
            {/* P158·5 — Surfaces how many duplicate cash rows we skipped
                (deposits / buys / sells / dividends already on file
                from a prior import). Only shows in append + skip
                duplicates. */}
            {(result.cash_skipped_dupes || 0) > 0 && (
              <p className="text-xs text-muted-foreground" data-testid="csv-success-cash-skipped">
                Skipped{' '}
                <span className="font-mono font-bold text-amber-600 dark:text-amber-400">{result.cash_skipped_dupes}</span>{' '}
                duplicate cash {result.cash_skipped_dupes === 1 ? 'transaction' : 'transactions'} already on file from a previous import.
              </p>
            )}
            {result.errors && result.errors.length > 0 && (
              <details className="text-left text-xs">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                  Show skipped rows ({result.errors.length})
                </summary>
                <ul className="mt-1 max-h-40 overflow-y-auto space-y-0.5 font-mono">
                  {result.errors.map((e, i) => (
                    <li key={i} className="text-rose-700 dark:text-rose-400" data-testid={`csv-error-${i}`}>
                      • {e.ticker}: {e.reason}
                    </li>
                  ))}
                </ul>
              </details>
            )}
            {/* Round P137 (Feb 2026) — Journal drafts breakdown. Only
                renders when the parser flagged rows for review (corrupt
                dates, unknown transaction types, open short positions).
                Backs up the toast so the count is discoverable inside
                the success modal too. */}
            {result.jnl_breakdown && result.jnl_breakdown.total > 0 && (
              <div
                className="text-left rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 px-3 py-2"
                data-testid="csv-success-jnl-breakdown"
              >
                <div className="flex items-center gap-2 mb-1">
                  <BookOpen className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                  <p className="text-xs font-bold text-amber-800 dark:text-amber-200">
                    {result.jnl_breakdown.total} row{result.jnl_breakdown.total === 1 ? '' : 's'} sent to Journal drafts
                  </p>
                </div>
                <ul className="text-[11px] text-amber-800 dark:text-amber-300 space-y-0.5 pl-5 list-disc">
                  {result.jnl_breakdown.CSV_WARN > 0 && (
                    <li data-testid="csv-jnl-warn">
                      <strong>{result.jnl_breakdown.CSV_WARN}</strong> parser warning{result.jnl_breakdown.CSV_WARN === 1 ? '' : 's'} (malformed date, truncated row, or non-numeric shares)
                    </li>
                  )}
                  {result.jnl_breakdown.CSV_UNKNOWN > 0 && (
                    <li data-testid="csv-jnl-unknown">
                      <strong>{result.jnl_breakdown.CSV_UNKNOWN}</strong> unrecognised transaction {result.jnl_breakdown.CSV_UNKNOWN === 1 ? 'type' : 'types'} (broker-specific codes we don&rsquo;t yet map)
                    </li>
                  )}
                  {result.jnl_breakdown.SHORT_OPEN > 0 && (
                    <li data-testid="csv-jnl-short-open">
                      <strong>{result.jnl_breakdown.SHORT_OPEN}</strong> open short position{result.jnl_breakdown.SHORT_OPEN === 1 ? '' : 's'} (portfolio schema tracks long only — recorded for reference)
                    </li>
                  )}
                  {result.jnl_breakdown.other > 0 && (
                    <li data-testid="csv-jnl-other">
                      <strong>{result.jnl_breakdown.other}</strong> other draft{result.jnl_breakdown.other === 1 ? '' : 's'}
                    </li>
                  )}
                </ul>
                <p className="text-[10px] text-amber-700 dark:text-amber-400 mt-1.5 leading-snug">
                  Review these under <span className="font-mono">/journal → Drafts</span> to reconcile with your broker statement.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Round 92o+++++++ — Per-batch account-type picker. Renders
            on the preview step so the user picks where these positions
            live BEFORE clicking Confirm Import. The selected value
            applies to every imported row. Round P108v — required. */}
        {step === 'preview' && (
          <div className="border-t border-border/40 px-1 pt-3 mt-3" data-testid="csv-account-type-row">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground block mb-1">
              Account Type for this batch <span className="text-rose-600 dark:text-rose-400">*</span>
            </label>
            <select
              value={acctTypeForBatch}
              onChange={(e) => setAcctTypeForBatch(e.target.value)}
              className={`w-full h-10 rounded-lg border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary ${missingAcctType ? 'border-rose-400 dark:border-rose-600 ring-1 ring-rose-400/40' : ''}`}
              data-testid="csv-account-type-select"
            >
              <option value="" disabled>— Pick an account type —</option>
              {ACCOUNT_TYPES.map((at) => (
                <option key={at.key} value={at.key}>
                  {at.label}{!at.niit_applies ? ' — NIIT-exempt' : ''}
                </option>
              ))}
            </select>
            {missingAcctType ? (
              <p className="text-[10px] font-semibold text-rose-600 dark:text-rose-400 mt-1" data-testid="csv-account-type-required">
                Required — pick one before importing. Positions imported without a tag end up as untagged Taxable on the tax audit pages.
              </p>
            ) : (
              <p className="text-[10px] text-muted-foreground mt-1">
                All rows imported in this batch will be tagged with this account type. You can re-tag any individual lot later from the portfolio page.
              </p>
            )}
          </div>
        )}

        {/* Round 92o++++++++N — Per-batch broker picker.
            Round P108v — no longer optional. */}
        {step === 'preview' && (
          <div className="border-t border-border/40 px-1 pt-3 mt-3" data-testid="csv-broker-row">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground block mb-1">
              Brokerage for this batch <span className="text-rose-600 dark:text-rose-400">*</span>
            </label>
            <select
              value={brokerForBatch}
              onChange={(e) => setBrokerForBatch(e.target.value)}
              className={`w-full h-10 rounded-lg border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary ${missingBroker ? 'border-rose-400 dark:border-rose-600 ring-1 ring-rose-400/40' : ''}`}
              data-testid="csv-broker-select"
            >
              <option value="" disabled>— Pick a brokerage —</option>
              {BROKERS.map((br) => (
                <option key={br.key} value={br.key}>{br.label}</option>
              ))}
            </select>
            {missingBroker ? (
              <p className="text-[10px] font-semibold text-rose-600 dark:text-rose-400 mt-1" data-testid="csv-broker-required">
                Required — pick the brokerage that holds these positions before importing.
              </p>
            ) : (
              <p className="text-[10px] text-muted-foreground mt-1">
                All rows imported in this batch will be tagged with this broker on the Tax Account Audit. You can re-tag per-position later.
              </p>
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 flex-wrap pt-2 border-t mt-2 -mx-4 sm:-mx-6 px-4 sm:px-6 pb-1" data-testid="csv-import-footer">
          {step === 'pick' && (
            <>
              <Button variant="outline" onClick={handleCancel} data-testid="csv-cancel-btn">
                {file ? 'Clear file' : 'Cancel'}
              </Button>
              <Button
                onClick={handleParse}
                disabled={!file || !mode || parseLoading}
                data-testid="csv-parse-btn"
              >
                {parseLoading ? 'Reading…' : 'Continue →'}
              </Button>
            </>
          )}
          {step === 'preview' && (
            <>
              <Button variant="outline" onClick={() => setStep('pick')} data-testid="csv-back-btn">← Back</Button>
              <Button
                onClick={handleImport}
                disabled={confirmDisabled}
                className={mode === 'overwrite' ? 'bg-rose-600 hover:bg-rose-700' : ''}
                data-testid="csv-confirm-import-btn"
              >
                {importLoading
                  ? 'Importing…'
                  : mode === 'overwrite'
                  ? `Overwrite & Import ${importableCount}${cashCount > 0 ? ` + ${cashCount} cash` : ''}`
                  : `Add ${importableCount}${cashCount > 0 ? ` + ${cashCount} cash` : ''} to Portfolio`}
              </Button>
            </>
          )}
          {step === 'done' && (
            <Button onClick={handleViewPortfolio} data-testid="csv-view-portfolio-btn">
              View your portfolio
            </Button>
          )}
        </div>
        </div>
      </div>
      <CsvColumnMapper
        open={mapperOpen}
        csvText={csvTextCache}
        portfolioId={portfolioId}
        onClose={() => setMapperOpen(false)}
        onPreview={(mappedPreview) => {
          // Default to "append" if user hadn't picked a mode yet
          if (!mode) setMode('append');
          setPreview(mappedPreview);
          setStep('preview');
        }}
      />
    </div>
    </>
  );
}
